import React from "react"

import { useQuery } from "react-query"

import { Warning } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { Tag, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"

import { InfoContainer } from "../../../components/core/InfoContainer"
import { LogViewer } from "../../../components/data-display/LogViewer"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { AutoMLJobLogs } from "../../../types/generated/api/AutoMLJobLogs"
import type { RetrainingJobLogs } from "../../../types/generated/api/RetrainingJobLogs"

type Props = {
  open: boolean
  projectId: string
  retrainingUUID: string
  isAutoMl?: boolean
  trainingName?: string
  trainingJobType?: string
  onClose: () => void
  jobStatus?: string
}

const StatusColorMap = {
  PENDING: "blue",
  RUNNING: "blue",
  SUCCEEDED: "green",
  FAILED: "red",
  TIMEOUT: "red",
  CANCELLED: "gray",
}

export function RetrainingLogsView(props: Readonly<Props>): React.ReactElement {
  const { projectId, retrainingUUID, open, onClose, jobStatus, trainingName, trainingJobType, isAutoMl } = props
  const muiTheme = useMuiTheme()
  const theme = getTheme()

  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("md"))

  const { isLoading: isRetrainingLogsLoading, data = { logs: [] } } = useQuery<RetrainingJobLogs, AxiosError>(
    ["retraining-logs", projectId, retrainingUUID],
    () => KonanAPI.fetchRetrainingModelLogs(projectId, retrainingUUID),
    {
      enabled: !!projectId && open && trainingJobType === "retraining" && !isAutoMl,
      refetchInterval: 10000,
    },
  )

  const { isLoading: isTrainingLogsLoading, data: trainingLogsData = { logs: [] } } = useQuery<
    RetrainingJobLogs,
    AxiosError
  >(["retraining-logs", projectId, retrainingUUID], () => KonanAPI.fetchTrainingModelLogs(projectId, retrainingUUID), {
    enabled: !!projectId && open && trainingJobType === "training" && !isAutoMl,
    refetchInterval: 10000,
  })

  const { isLoading: isTrainingAutomlLogsLoading, data: trainingAutomlLogs } = useQuery<AutoMLJobLogs, AxiosError>(
    ["training-automl-logs", projectId, retrainingUUID],
    () => KonanAPI.fetchAutoMLTrainingJobLogs(projectId, retrainingUUID),
    {
      enabled: !!projectId && open && isAutoMl,
      refetchInterval: !isAutoMl ? false : 10000,
    },
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle className="dialog-header-base">
        <Grid item container xs={12} justifyContent="space-between">
          <Grid item>
            <Tag
              size="small"
              color={StatusColorMap[jobStatus as keyof typeof StatusColorMap] as "blue" | "green" | "red" | "gray"}
            >
              {jobStatus?.toLowerCase()}
            </Tag>
          </Grid>

          <Grid item>
            <IconButton size="small" onClick={() => onClose()} className={"close-icon-button"}>
              <CloseIcon style={{ color: theme.palette.gray.background[1] }} />
            </IconButton>
          </Grid>
        </Grid>

        <Typography variant="h1-bold">{trainingName}</Typography>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        <Grid>
          <Typography variant="h2-bold">Training Logs</Typography>
        </Grid>

        {trainingJobType === "training" ? (
          isTrainingLogsLoading ? (
            <Grid
              container
              justifyContent="center"
              alignContent="center"
              className={"logs-grid-height"}
              marginBottom={2}
            >
              <InfoContainer
                icon={<CircularProgress className={"logs-circular-progress"} />}
                title="Loading trainings Logs..."
              />
            </Grid>
          ) : data.logs !== null && data.logs?.length ? (
            <Grid item direction="row" xs={12} mb={1}>
              <LogViewer data={trainingLogsData.logs as string[]} isLoading={isTrainingLogsLoading} />
            </Grid>
          ) : (
            <Grid
              container
              justifyContent="center"
              alignContent="center"
              className={"logs-grid-height"}
              marginBottom={2}
            >
              <InfoContainer
                icon={<Warning fontSize="large" className={"warning-icon-base"} />}
                title="No training/retraining logs to display."
              />
            </Grid>
          )
        ) : isAutoMl ? (
          isTrainingAutomlLogsLoading ? (
            <Grid
              container
              justifyContent="center"
              alignContent="center"
              className={"logs-grid-height"}
              marginBottom={2}
            >
              <InfoContainer
                icon={<CircularProgress className={"logs-circular-progress"} />}
                title="Loading retraining Logs..."
              />
            </Grid>
          ) : trainingAutomlLogs && trainingAutomlLogs.logs !== null && trainingAutomlLogs.logs?.length ? (
            <Grid container item direction="row" marginBottom={2}>
              <LogViewer data={trainingAutomlLogs && trainingAutomlLogs.logs} isLoading={isTrainingAutomlLogsLoading} />
            </Grid>
          ) : (
            <Grid
              container
              justifyContent="center"
              alignContent="center"
              className={"logs-grid-height"}
              marginBottom={2}
            >
              <InfoContainer
                icon={<Warning fontSize="large" className={"warning-icon-base"} />}
                title="No training/retraining logs to display."
              />
            </Grid>
          )
        ) : isRetrainingLogsLoading ? (
          <Grid container justifyContent="center" alignContent="center" className={"logs-grid-height"} marginBottom={2}>
            <InfoContainer
              icon={<CircularProgress className={"logs-circular-progress"} />}
              title="Loading retraining Logs..."
            />
          </Grid>
        ) : data.logs !== null && data.logs?.length ? (
          <Grid container item direction="row" marginBottom={2}>
            <LogViewer data={data.logs} isLoading={isRetrainingLogsLoading} />
          </Grid>
        ) : (
          <Grid container justifyContent="center" alignContent="center" className={"logs-grid-height"} marginBottom={2}>
            <InfoContainer
              icon={<Warning fontSize="large" className={"warning-icon-base"} />}
              title="No training/retraining logs to display."
            />
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}
